import {observer} from "mobx-react"
import moment from "moment"
import React from "react"
import {Alert, ProgressBar} from "react-bootstrap"
import {connect} from "react-redux"
import {authStore} from "../../app/common/authentication/AuthStore"
import {RouteComponentProps} from "react-router"
import {Dispatch} from "redux"
import {loadingSpinnerStore} from "../../app/common/loaders/LoadingSpinnerStore"
import {productStore} from "../../app/common/products/ProductStore"
import {Section} from "../../app/section/models/Section"
import {sectionStore} from "../../app/section/SectionStore"
import {updateSection} from "../../redux/item/actions"
import HelperService from "../../services/HelperService"
import PTHandler from "../../services/PTHandler"
import {
    ApiErrorResponse,
    ApiItemResponse,
    ApiPanelGraph,
    ApiPanelGraphEdge,
    ApiTakePanelStateEnum,
    Panel,
    PanelSkillEnum,
    State,
    Take,
    TakePanel,
    TestState
} from "../../types/types"
import {
    APT_PRODUCT_ID,
    ProctorTypes,
    STAMP_4S_PRODUCT_ID,
    STAMP_4SE_PRODUCT_ID,
    SUPPORT_MESSAGE,
    STAMP_SIGN_LANGUAGE_PRODUCT_ID
} from "../../util/Constants"
import {PanelId, TakeId} from "../../validation/ValidPrimaryKey"
import {addTakeDispatches, avantHistory, TakeDispatches} from "../App/App"
import Button from "../Button/Button"
import StartTestModal from "../StartTestModal/StartTestModal"
import ApiService from "../../services/ApiService"
import {getProgress} from "../StatusBar/StatusBar"
import {RoutePaths} from "../../app/routes/Routes"
import TestTakerAgreementModal from "./TestTakerAgreement"
import LanguageUtils from "../../util/LanguageUtils"

interface StateToProps {
    section: Section | null
    take: Take
    takeCode: string
    testState: TestState
    panelGraph: ApiPanelGraph
}

function mapStateToProps(state: State): StateToProps {
    return {
        section: state.item.currentSection,
        take: state.app.take!,
        takeCode: state.app.takeCode,
        testState: state.app.testState!,
        panelGraph: state.app.panelGraph!
    }
}

// @ts-ignore
async function endPTSession() {
    await PTHandler.endExam()
}

interface DispatchToProps extends TakeDispatches {
    updateSectionDispatch: (bin: Section) => void
}

function mapDispatchToProps(dispatch: Dispatch): DispatchToProps {
    const dispatches = {
        updateSectionDispatch: (section: Section) => {
            dispatch(updateSection(section))
        }
    }
    return addTakeDispatches(dispatches, dispatch)
}

interface DashboardProps extends StateToProps, DispatchToProps, RouteComponentProps {}

interface DashboardState {
    error: boolean | string
    loaded: boolean
    take: Take
    showModal: boolean
    currentPanelId: number
    currentTestName: string
    currentSection: Section | null
    triedToStartPanel: boolean
}

@observer
class Dashboard extends React.Component<DashboardProps, DashboardState> {
    state: DashboardState
    private skillToTestName: Map<string, string> = new Map([
        [PanelSkillEnum.WRITING, "Writing"],
        [PanelSkillEnum.READING, "Reading"],
        [PanelSkillEnum.LISTENING, "Listening"],
        [PanelSkillEnum.SPEAKING, "Speaking"]
    ])

    constructor(props: DashboardProps) {
        super(props)
        this.state = {
            error: false,
            loaded: false,
            take: props.take,
            showModal: false,
            currentPanelId: -1,
            currentTestName: "",
            currentSection: null,
            triedToStartPanel: false
        }
    }

    async componentWillMount() {
        authStore.didTheyAgree()
    }

    async componentDidMount() {
        HelperService.enableTextSelection()
        await this.getData()
        //This spot guarantees that LOGGED_IN gets set, this is important for
        //past uses that log in new.   This can possibly later be taken out.
        ApiService.updateTakeState(this.props.take.id, "LOGGED_IN")
    }

    async componentDidUpdate() {
        await this.getData()
    }

    getData = async () => {
        const driver = productStore.driver
        const {loaded, take, currentSection} = this.state
        if (driver && !loaded) {
            const product = productStore.loginProduct
            this.setState({loaded: true})
            document.title = `${driver.config.PRODUCT_TITLE} | Dashboard`
            let lastPage = HelperService.getLastPageFromLs()
            if (lastPage) {
                try {
                    if (product == null) {
                        throw Error("Can't get test state while product is null.")
                    }
                    const testState = await driver.helper.refreshTest(this.props, lastPage, product)
                    if (!testState.take) {
                        throw Error("[Dashboard.componentDidMount] testState.take is null or undefined")
                    }
                    loadingSpinnerStore.hideLoadingSpinner = true
                    this.setState({take: testState.take})
                } catch (error) {
                    this.setState({
                        error: SUPPORT_MESSAGE
                    })
                    throw new Error(
                        `Failed to refresh test. ${error.response.data.error} - ${error.response.data.message}`
                    )
                }
            } else {
                lastPage = {
                    url: "/dashboard",
                    takeCode: this.props.takeCode,
                    panelGraphId: this.props.panelGraph.id
                }
                localStorage.setItem("lastPage", JSON.stringify(lastPage))
            }
        }

        if (take && currentSection == null) {
            const inProgressSkillList = take.takePanels.filter((p) => p.panelState === ApiTakePanelStateEnum.STARTED)

            if (inProgressSkillList.length > 1) {
                throw new Error(`There should never be more than 1 skill in progress at a time!`)
            }

            const inProgressSkill = inProgressSkillList.length > 0 ? inProgressSkillList[0] : null

            if (inProgressSkill) {
                sectionStore.findCurrentSection(new TakeId(take.id), new PanelId(inProgressSkill.panelId)).then(
                    (section: Section) => {
                        this.setState({currentSection: section})
                    },
                    (err: ApiErrorResponse) => {
                        this.setState({
                            error: SUPPORT_MESSAGE
                        })
                        throw new Error(
                            `Failed to get panel. ${err.response.data.error} - ${err.response.data.message}`
                        )
                    }
                )
            }
        }
    }

    openModal = (currentPanelId: number, currentTestName: string) => {
        this.setState({showModal: true, currentPanelId, currentTestName})
    }

    closeModal = () => {
        this.setState({showModal: false})
    }

    logout = () => {
        HelperService.logout(undefined)
    }

    agreeToEULA = () => {
        authStore.agreeToEula()
    }

    disagree = () => {
        const authUser = authStore && authStore.auth ? authStore.auth : null
        const isSSOLogin = authUser && authUser.ssoId ? true : false

        if (isSSOLogin) avantHistory.push(RoutePaths.SSO_STUDENT)
        else this.logout()
    }

    startPanel = () => {
        const {take, currentPanelId, currentTestName, triedToStartPanel} = this.state
        const {updateSectionDispatch, history} = this.props
        const driver = productStore.driver

        this.setState({showModal: false})
        sectionStore.findCurrentSection(new TakeId(take.id), new PanelId(currentPanelId)).then(
            (section: Section) => {
                if (section.items.length > 0) {
                    section.config = driver!!.config.TEST_ENGINE_CONFIG
                    section.config.testName = currentTestName
                    updateSectionDispatch(section)
                    const url: string = "/item"
                    this.setState({triedToStartPanel: false})
                    history.push(url)
                } else {
                    if (!triedToStartPanel) {
                        this.setState({triedToStartPanel: true})
                        this.startPanel()
                    } else {
                        this.setState({
                            error: SUPPORT_MESSAGE
                        })
                        throw new Error("No items returned from current section after two tries")
                    }
                }
            },
            (err: ApiErrorResponse) => {
                this.setState({
                    error: SUPPORT_MESSAGE
                })
                throw new Error(`Failed to get panel. ${err.response.data.error} - ${err.response.data.message}`)
            }
        )
    }

    renderStartedSkill = (startedPanel: Panel, startedTakePanel: TakePanel) => {
        let progress: number[] = []

        if (this.state.currentSection) {
            // Sort the responses for this skill by id to get the most recent response
            startedTakePanel.responses.sort((a: ApiItemResponse, b: ApiItemResponse) => b.id - a.id)
            const lastResponse = startedTakePanel.responses[0]
            const itemId = lastResponse ? lastResponse.itemId : null
            const progressInSection = HelperService.getProgressInSection(this.state.currentSection, itemId, true)
            progress = getProgress(this.state.currentSection, progressInSection)
        }
        const is4Se = productStore.driver!!.productId.value() === STAMP_4SE_PRODUCT_ID.value()

        const continueBaseClass = "dashboard__started-skill__content__image"
        const continueClass = is4Se ? `${continueBaseClass} ${continueBaseClass}__4se` : continueBaseClass

        const name = this.skillToTestName.get(startedPanel.skill)
        const testName = name ? name : ""

        // I've refactored this function to display the Listening/ASL Receptive image when the section is a
        // Listening section and the test is a 4s test. This is a temporary change and will be removed later.
        const imageUrl = (is4Se: boolean) => {
            if (is4Se) {
                return `https://cdn.avantassessment.com/resources/noella_images/${testName.toLowerCase()}_small.jpg`
            } else if (testName === "Listening" && this.props.panelGraph.productId === STAMP_4S_PRODUCT_ID.value()) {
                return "https://cdn.avantassessment.com/resources/common/img/img-2020/Listening-asl-receptive.svg"
            } else if (testName === "Listening" && this.props.panelGraph.productId === STAMP_SIGN_LANGUAGE_PRODUCT_ID.value()) {
                return "https://cdn.avantassessment.com/resources/common/img/img-2020/Receptive.svg"
            }
            else if (testName === "Speaking" && this.props.panelGraph.productId === STAMP_SIGN_LANGUAGE_PRODUCT_ID.value()) {
                return "https://cdn.avantassessment.com/resources/common/img/img-2020/Expressive.svg"
            }
            else {
                return `https://cdn.avantassessment.com/resources/common/img/img-2020/${testName}.svg`
            }
        }

        return (
            <div className="dashboard__started-skill">
                <div className="dashboard__started-skill__content">
                    <div className={continueClass}>
                        <img src={imageUrl(is4Se)} alt="" />
                    </div>
                    <div>
                        <h4 className="dashboard__started-skill__content__title">
                        {testName === "Listening" && this.props.panelGraph.productId === STAMP_4S_PRODUCT_ID.value()
                            ? "Listening / ASL Receptive"
                            :testName === "Listening" && this.props.panelGraph.productId === STAMP_SIGN_LANGUAGE_PRODUCT_ID.value()?"Receptive":testName === "Speaking" && this.props.panelGraph.productId === STAMP_SIGN_LANGUAGE_PRODUCT_ID.value()?"Expressive": testName}

                        </h4>
                        <div className="dashboard__started-skill__content__progress">
                            {progress.map((progressInSection: number, index: number) => {
                                return (
                                    <ProgressBar
                                        key={index + 1}
                                        className="dashboard__started-skill__content__progress__bar"
                                        now={progressInSection}
                                    />
                                )
                            })}
                            <p className="dashboard__started-skill__content__progress__text" style={{display: "none"}}>
                                {`${startedTakePanel.currentSection - 1} / ${
                                    startedTakePanel.numSections
                                } section(s) complete`}
                            </p>
                        </div>
                        <div className="dashboard__started-skill__content__buttons">
                            <Button
                                className="avant-button--primary dashboard__started-skill__content__buttons__button"
                                block={true}
                                testId="modal-yes-button"
                                onClick={() => this.openModal(startedPanel!.id, testName)}
                            >
                                CONTINUE
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    renderSkill = (
        availablePanels: Map<string, Panel>,
        panelSkills: Set<string>,
        skill: string,
        testName: string,
        takePanels: TakePanel[],
        completedPanelSkills: Set<string>
    ) => {
        const inProgressSkillList = takePanels
            .filter((p) => p.panelState === ApiTakePanelStateEnum.STARTED)
            .map((p) => p.panel.skill)

        if (inProgressSkillList.length > 1) {
            throw new Error(`There should never be more than 1 available panel at a time!`)
        }
        const inProgressSkill = inProgressSkillList.length > 0 ? inProgressSkillList[0] : null
        const isHandwritten = this.props.panelGraph.handwritten && skill === PanelSkillEnum.WRITING
        const panel = availablePanels.get(skill)
        const is4se = productStore.driver!!.productId.value() === STAMP_4SE_PRODUCT_ID.value()
        const isEnabled =
            (inProgressSkill // Are we in-progress?
                ? panel && panel.skill === inProgressSkill // Yup, only the skill that is in-progress is enabled
                : !!panel) && // Otherwise we haven't started a skill, only the ones that are in the available panels are enabled
            !isHandwritten // BUT if the panel is handwritten, it is disabled.
        const disabledClass = isEnabled ? "" : "dashboard__skills__skill-container--disabled"
        const handwrittenClass = isHandwritten ? "dashboard__skills__skill-container--handwritten" : ""
        const seBackground = is4se ? "dashboard__skills__skill-container--4se" : ""
        const seDisabledBackground = is4se ? "dashboard__skills__skill-container__completed-skill--4se" : ""
        const containerClass = `dashboard__skills__skill-container ${disabledClass} ${handwrittenClass} ${seBackground}`
        const completedSkill = `dashboard__skills__skill-container__completed-skill ${seDisabledBackground}`
        const takePanel = takePanels.find((tp: TakePanel) => {
            return tp.panel.skill === skill
        })

        const isComplete = (takePanel && takePanel.panelState === ApiTakePanelStateEnum.COMPLETED) || false
        let imageUrl = `https://cdn.avantassessment.com/resources/common/img/img-2020/${testName}.svg`
        let imageClass = "dashboard__skills__skill-container__skill"
        if (productStore.driver!!.productId.value() === STAMP_4SE_PRODUCT_ID.value()) {
            imageUrl = `https://cdn.avantassessment.com/resources/noella_images/${testName.toLowerCase()}_small.jpg`
            imageClass = `${imageClass} dashboard__skills__skill-container__skill--4se`
        }

        // I've included this statement to display the Listening/ASL Receptive image when the section is a
        // Listening section and the test is a 4s test. This is a temporary change and will be removed later.
        if (testName === "Listening" && this.props.panelGraph.productId === STAMP_4S_PRODUCT_ID.value()) {
            imageUrl = "https://cdn.avantassessment.com/resources/common/img/img-2020/Listening-asl-receptive.svg"
        }
        else if (testName === "Listening" && this.props.panelGraph.productId === STAMP_SIGN_LANGUAGE_PRODUCT_ID.value()) {
            imageUrl = "https://cdn.avantassessment.com/resources/common/img/img-2020/Receptive.svg"
        } else if (testName === "Speaking" && this.props.panelGraph.productId === STAMP_SIGN_LANGUAGE_PRODUCT_ID.value()) {
            imageUrl = "https://cdn.avantassessment.com/resources/common/img/img-2020/Expressive.svg"
         }else{}

        // On 4s test dashboards, this function returns "Listening/ASL Receptive must be completed before Speaking"
        // underneath the speaking skill image. This is a temporary function and will be changed later.
        const mustCompleteExamText = (testSection: string, currentProductId: number) => {
            if (testSection === "Speaking" && currentProductId === STAMP_4S_PRODUCT_ID.value()) {
                return `Listening/ASL Receptive must be completed before ${testSection}`
            } else if (testSection === "Speaking" && currentProductId === STAMP_SIGN_LANGUAGE_PRODUCT_ID.value()) {
                return `Receptive must be completed before Expressive`
            } else if (testSection === "Speaking" && currentProductId !== STAMP_SIGN_LANGUAGE_PRODUCT_ID.value()) {
                return `Listening must be completed before ${testSection}`
            } else {
                return `Reading must be completed before ${testSection}`
            }
        }

        return (
            <div
                data-tst-id={`skill-${skill}`}
                data-panel-id={`panel.id`}
                className={containerClass}
                onClick={() => {
                    if (isEnabled) {
                        this.openModal(panel!.id, testName)
                    }
                }}
                onKeyPress={() => {
                    if (isEnabled) {
                        this.openModal(panel!.id, testName)
                    }
                }}
            >
                <div className={imageClass} tabIndex={0}>
                    {!isHandwritten ? (
                        <img src={imageUrl} alt="" />
                    ) : (
                        <div className="dashboard__skills__skill-container__skill__handwritten">
                            Ask Your Teacher/Proctor for the Writing Prompts
                        </div>
                    )}
                </div>
                {isComplete && is4se && (
                    <div className={completedSkill}>
                        <div className="dashboard__skills__skill-container__completed-skill__circle">
                            <i
                                className={
                                    "material-icons " +
                                    "dashboard__skills__skill-container__completed-skill__circle__4se_check"
                                }
                            >
                                done
                            </i>
                        </div>
                    </div>
                )}

                {isComplete && !is4se && (
                    <div className={completedSkill}>
                        <img
                            src="https://cdn.avantassessment.com/resources/common/img/img-2020/checkmark.svg"
                            className="dashboard__skills__skill-container__completed-skill__check"
                        />
                    </div>
                )}
                {/*The conditional statement below renders "Listening ASL Receptive" when the skill is listening
                and the exam is a 4s exam. This is a temporary change and will be reversed later.*/}
                {testName === "Listening" && this.props.panelGraph.productId === STAMP_4S_PRODUCT_ID.value() ? (
                    <>
                        <h4 className="dashboard__skills__skill-container__title">Listening</h4>
                        <h4 className="dashboard__skills__skill-container__title">ASL Receptive</h4>
                    </>
                ) : testName === "Listening" &&
                  this.props.panelGraph.productId === STAMP_SIGN_LANGUAGE_PRODUCT_ID.value() ? (
                    <>
                        <h4 className="dashboard__skills__skill-container__title">Receptive</h4>
                    </>
                ) : testName === "Speaking" &&
                  this.props.panelGraph.productId === STAMP_SIGN_LANGUAGE_PRODUCT_ID.value() ? (
                    <>
                        <h4 className="dashboard__skills__skill-container__title">Expressive</h4>
                    </>
                ) : (
                    <h4 className="dashboard__skills__skill-container__title">{testName}</h4>
                )}
                {!isEnabled &&
                    !is4se &&
                    !isComplete &&
                    (testName === "Speaking" || testName === "Writing") &&
                    //Ensure that when a test is in progress, the message only appears for tests that still haven't had their prerequisite met
                    !completedPanelSkills.has(
                        skill === PanelSkillEnum.WRITING ? PanelSkillEnum.READING : PanelSkillEnum.LISTENING
                    ) && (
                        <p className="dashboard__skills__skill-container__completed-progress__text">
                            {mustCompleteExamText(testName, this.props.panelGraph.productId)}
                        </p>
                    )}
                {isComplete && (
                    <div className="dashboard__skills__skill-container__completed-progress">
                        <div className="dashboard__skills__skill-container__completed-progress__progress" />
                        <p className="dashboard__skills__skill-container__completed-progress__text__opaque">
                            100% Completed
                        </p>
                    </div>
                )}
            </div>
        )
    }

    renderDashboardContainer = () => {
        const {panelGraph} = this.props
        const {take} = this.state
        const profile = take.profile

        const authUser = authStore && authStore.auth ? authStore.auth : null
        let firstName = ""
        let lastName = ""
        if (profile) {
            if (profile.firstName) {
                firstName = profile.firstName
            }
            if (profile.lastName) {
                lastName = profile.lastName
            }
        }
        const date = moment(take.created).format("MMMM D, YYYY")

        const panelSkills = new Set<string>()
        panelGraph.panelGraphEdges.forEach((edge: ApiPanelGraphEdge) => {
            const panel = edge.panelByNextPanelId
            if (panel.skill === undefined) {
                throw new Error("Panel skill is undefined")
            }
            panelSkills.add(panel.skill)
        })
        const availablePanels = new Map<string, Panel>()
        take.availablePanels.forEach((panel: Panel) => {
            if (panel.skill === undefined) {
                throw new Error("Panel skill is undefined")
            }
            availablePanels.set(panel.skill, panel)
        })

        let startedPanel: Panel | undefined
        let startedTakePanel: TakePanel | undefined
        const completedPanelSkills = new Set<string>()
        take.takePanels.forEach((takePanel: TakePanel) => {
            if (takePanel.panelState === ApiTakePanelStateEnum.STARTED) {
                startedPanel = takePanel.panel
                startedTakePanel = takePanel
            }
            if (takePanel.panelState === ApiTakePanelStateEnum.COMPLETED) {
                completedPanelSkills.add(takePanel.panel.skill)
            }
        })

        let languageName = LanguageUtils.removeTilde(productStore.loginProduct!!.contentAreaName)
        languageName = LanguageUtils.removeMonolingual(languageName)

        const testName = productStore.loginProduct == null ? "Test" : languageName + " test"

        completedPanelSkills.forEach((skill: string) => {
            if (availablePanels.has(skill)) {
                availablePanels.delete(skill)
            }
        })

        const isProctorTrack =
            productStore.loginProduct!.proctorType === ProctorTypes.PROCTORTRACK.valueOf() ||
            productStore.loginProduct!.proctorType === ProctorTypes.AVANTPROCTOR.valueOf() ||
            productStore.loginProduct!.proctorType === ProctorTypes.EDUPROCTOR.valueOf()
        const isSSOLogin = authUser && authUser.ssoId ? true : false
        const isComplete = availablePanels.size === 0

        const is4Se = productStore.driver!!.productId.value() === STAMP_4SE_PRODUCT_ID.value()
        const isAPT = productStore.driver!!.productId.value() === APT_PRODUCT_ID.value()

        // Leading space intentionally placed
        let fullName = ""
        if (firstName || lastName) {
            fullName = ` ${firstName} ${lastName}`
        }

        const renderReading =
            ((startedPanel !== undefined && startedPanel.skill !== PanelSkillEnum.READING) ||
                startedPanel === undefined) &&
            panelSkills.has(PanelSkillEnum.READING)
        const renderWriting =
            ((startedPanel !== undefined && startedPanel.skill !== PanelSkillEnum.WRITING) ||
                startedPanel === undefined) &&
            panelSkills.has(PanelSkillEnum.WRITING)
        const renderListening =
            ((startedPanel !== undefined && startedPanel.skill !== PanelSkillEnum.LISTENING) ||
                startedPanel === undefined) &&
            panelSkills.has(PanelSkillEnum.LISTENING)
        const renderSpeaking =
            ((startedPanel !== undefined && startedPanel.skill !== PanelSkillEnum.SPEAKING) ||
                startedPanel === undefined) &&
            panelSkills.has(PanelSkillEnum.SPEAKING)

        return (
            <>
                {!isComplete ? (
                    <>
                        <h3 className="dashboard__title">{`Welcome${fullName}!`}</h3>
                        <p className="dashboard__date">{`${testName} started ${date}`}</p>

                        {!is4Se && !isAPT && (
                            <TestTakerAgreementModal testName={"STAMP"} agree={() => this.agreeToEULA()} />
                        )}

                        {!isSSOLogin && (
                            <div className="dashboard__not-you">
                                Not you?
                                {"\u00A0"}
                                <a href="javascript:void(0)" className="dashboard__not-you__link" onClick={this.logout}>
                                    Click to return to login page
                                </a>
                            </div>
                        )}

                        {is4Se && (
                            <h4 className="dashboard__click-animal">Click on the animal the proctor tells you to.</h4>
                        )}
                    </>
                ) : (
                    <>
                        <p className="dashboard__title">Congratulations! You have completed the test!</p>
                        <p className="dashboard__subtitle">All of your answers have been saved.</p>
                        {isProctorTrack && setTimeout(endPTSession, 5 * 1000) && (
                            <>
                                <p className="dashboard__subtitle">
                                    We will attempt to shutdown the proctored session in 30 seconds.
                                </p>
                                <script>console.log("beforeTimeout");setTimeout(endPTSession, 5 * 1000);</script>
                            </>
                        )}
                    </>
                )}
                {startedPanel !== undefined &&
                    startedTakePanel !== undefined &&
                    this.renderStartedSkill(startedPanel, startedTakePanel)}
                <div className={"dashboard__skills"}>
                    {renderReading &&
                        this.renderSkill(
                            availablePanels,
                            panelSkills,
                            PanelSkillEnum.READING,
                            "Reading",
                            take.takePanels,
                            completedPanelSkills
                        )}
                    {renderWriting &&
                        this.renderSkill(
                            availablePanels,
                            panelSkills,
                            PanelSkillEnum.WRITING,
                            "Writing",
                            take.takePanels,
                            completedPanelSkills
                        )}
                    {renderListening &&
                        this.renderSkill(
                            availablePanels,
                            panelSkills,
                            PanelSkillEnum.LISTENING,
                            "Listening",
                            take.takePanels,
                            completedPanelSkills
                        )}
                    {renderSpeaking &&
                        this.renderSkill(
                            availablePanels,
                            panelSkills,
                            PanelSkillEnum.SPEAKING,
                            "Speaking",
                            take.takePanels,
                            completedPanelSkills
                        )}
                </div>
                {isComplete && (
                    <div>
                        <Button type="button" className="avant-button--primary dashboard__button" onClick={this.logout}>
                            LOGOUT
                        </Button>
                    </div>
                )}
            </>
        )
    }

    render() {
        const driver = productStore.driver
        const {error, take, showModal, currentTestName} = this.state
        const is4Se = productStore.driver!!.productId.value() === STAMP_4SE_PRODUCT_ID.value()
        const isAPT = productStore.driver!!.productId.value() === APT_PRODUCT_ID.value()

        return (
            <div className="dashboard">
                {error && (
                    <div className="alerts">
                        <Alert variant="danger" onClose={() => this.setState({error: false})}>
                            {error}
                        </Alert>
                    </div>
                )}
                {driver && take ? (
                    <>
                        <div className="center-text" id="content">
                            {this.renderDashboardContainer()}
                        </div>
                        {(authStore.agreedToEULA || is4Se || isAPT) && (
                            <StartTestModal
                                showModal={showModal}
                                currentTestName={currentTestName}
                                closeModal={this.closeModal}
                                startPanel={this.startPanel}
                                productId={driver.productId}
                            />
                        )}
                    </>
                ) : (
                    <div className="center-text" style={{fontWeight: "normal"}}>
                        <h2>Loading...</h2>
                    </div>
                )}
            </div>
        )
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard)
